// src/context/TenantContext.jsx
import { createContext, useContext, useEffect, useState } from 'react'
import api from '../api/client'

const TenantContext = createContext(null)

// Default Fabrinza brand (shown while loading)
const FABRINZA_DEFAULTS = {
  brand_name:      'Fabrinza',
  brand_tagline:   'India\'s Premier Textile Marketplace',
  logo_url:        null,
  favicon_url:     null,
  primary_color:   '#FF3D7F',
  secondary_color: '#FF8500',
  font_heading:    'Playfair Display',
  font_body:       'DM Sans',
  plan:            'enterprise',
  settings:        {},
  is_trial:        false,
}

export function TenantProvider({ children }) {
  const [tenant, setTenant]   = useState(FABRINZA_DEFAULTS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/tenant/config')
      .then(res => {
        const data = res.data?.data ?? res.data
        setTenant({ ...FABRINZA_DEFAULTS, ...data })
        applyTheme(data)
      })
      .catch(() => {
        // On error, fall back to Fabrinza defaults — app still works
        applyTheme(FABRINZA_DEFAULTS)
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <TenantContext.Provider value={{ tenant, setTenant, loading }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider')
  return ctx
}

/**
 * Inject CSS variables + fonts from tenant config.
 * Every component using var(--primary) automatically respects the tenant brand.
 */
function applyTheme(config) {
  const root = document.documentElement
  root.style.setProperty('--primary',   config.primary_color   ?? '#FF3D7F')
  root.style.setProperty('--secondary', config.secondary_color ?? '#FF8500')
  root.style.setProperty('--font-heading', `"${config.font_heading ?? 'Playfair Display'}"`)
  root.style.setProperty('--font-body',    `"${config.font_body    ?? 'DM Sans'}"`)

  // Page title
  if (config.brand_name) {
    document.title = config.brand_name
  }

  // Favicon
  if (config.favicon_url) {
    let link = document.querySelector("link[rel~='icon']")
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
    link.href = config.favicon_url
  }

  // Load custom fonts if different from defaults
  const needsFonts = [config.font_heading, config.font_body]
    .filter(Boolean)
    .filter(f => !['Playfair Display', 'DM Sans', 'Syne'].includes(f))

  if (needsFonts.length) {
    const link = document.createElement('link')
    link.rel  = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${needsFonts.map(f => f.replace(/ /g, '+')).join('&family=')}&display=swap`
    document.head.appendChild(link)
  }
}
